import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DetailRow, DetailRows, PartnerPartnerDetails, Skeleton, SubPartnerAccount, withStyles } from 'vopay';
import { BodyText, Title } from 'UI';
import { useToast } from '@/providers/Toast.provider';
import useCountryLabels from '@/hooks/useCountryLabels';
import useWebhookEventLabels from '@/hooks/useWebhookEventLabels';
import useFetchPartnerExtendedInfo, { type PartnerExtendedInfo } from '@/hooks/useApi/useFetchPartnerExtendedInfo';
import useFetchPartnerWebhookInfo from '@/hooks/useApi/useFetchPartnerWebhookInfo';
import toProvinceCode from '@/utils/toProvinceCode.helper';
import DetailsModule from '@/components/Entities/UI/DetailsModule/DetailsModule';
import ContactCard, { type Contact, type ContactRole } from '@/components/Entities/UI/ContactCard/ContactCard';
import { pickDetail, renderDetailValue } from '@/components/Entities/UI/DetailValue/detailValue';
import usePartnerAccountInfoRows from '@/components/Partners/PartnerManagement/usePartnerAccountInfoRows';
import {
  canReadPartnerExtendedInfo,
  canReadPartnerWebhooks,
} from '@/components/Partners/PartnerManagement/partnerSelfScopedReads';
import styles from './PartnerDetailsTab.module.scss';

const cn = withStyles(styles);

type PartnerDetailsTabProps = {
  partner: SubPartnerAccount;
  /** Undefined while the details load, null when the request failed. */
  details: PartnerPartnerDetails | null | undefined;
  /** The partner account's own bearer, which the extended-info and webhook routes authenticate as. */
  bearer: string;
};

/** The response's contact fields are `<Role>Contact<Field>`; the card reads a flat contact. */
const CONTACT_PREFIXES: Record<
  ContactRole,
  'PrimaryContact' | 'BillingContact' | 'TechnicalContact' | 'SupportContact'
> = {
  primary: 'PrimaryContact',
  billing: 'BillingContact',
  technical: 'TechnicalContact',
  support: 'SupportContact',
};

const CONTACT_ROLES: ContactRole[] = ['primary', 'billing', 'technical', 'support'];

const BLANK_CONTACT: Contact = { fullName: '', occupation: '', title: '', email: '', phoneNumber: '' };

/**
 * The partner page's Details tab (spec 5.6.1): the Account Info the drawer also shows, the business
 * details a partner account completes after creation, the webhook events it subscribes to, and its
 * four contacts. Account Info reads the list row and the details request the page already made;
 * the rest is read here with the partner account's own token, since `/partner/extended-info` and
 * `/partner/webhook-url/info` are self-scoped — and only in the states those routes accept, so a
 * Rejected or still-onboarding partner account reads as not available rather than as an error.
 */
export default function PartnerDetailsTab({ partner, details, bearer }: PartnerDetailsTabProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.PartnerManagement.PartnerDetailsTab' });
  const toast = useToast();
  const { resolveCountryLabel } = useCountryLabels();
  const { resolveWebhookEventLabel } = useWebhookEventLabels();
  const accountInfoRows = usePartnerAccountInfoRows(partner, details);
  const { partnerExtendedInfo, fetchPartnerExtendedInfo, reset: resetExtendedInfo } = useFetchPartnerExtendedInfo();
  const { partnerWebhooks, fetchPartnerWebhookInfo, reset: resetWebhooks } = useFetchPartnerWebhookInfo();
  const [hasExtendedInfoFailed, setHasExtendedInfoFailed] = useState(false);
  const [hasWebhooksFailed, setHasWebhooksFailed] = useState(false);

  const isExtendedInfoReadable = canReadPartnerExtendedInfo(partner);
  const isWebhooksReadable = canReadPartnerWebhooks(partner);

  useEffect(() => {
    resetExtendedInfo();
    resetWebhooks();

    // A row the list returned without a token cannot sign either read; both show as unavailable
    // rather than loading forever.
    setHasExtendedInfoFailed(!bearer);
    setHasWebhooksFailed(!bearer);

    if (!bearer) return undefined;

    // The hooks drop a superseded response themselves, but a rejection still reaches this caller —
    // so a request for the partner account the user has since left must not mark this one failed.
    let isCurrentPartner = true;

    // Each hook has logged its own cause; the module it feeds shows as unavailable.
    if (isExtendedInfoReadable) {
      fetchPartnerExtendedInfo(bearer).catch((error) => {
        if (!isCurrentPartner) return;

        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        setHasExtendedInfoFailed(true);

        toast.neutral(
          t('extendedInfoError', {
            defaultValue: 'Could not load the business details and contacts: {{errorMessage}}',
            errorMessage,
          }),
        );
      });
    }

    if (isWebhooksReadable) {
      fetchPartnerWebhookInfo(bearer).catch((error) => {
        if (!isCurrentPartner) return;

        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        setHasWebhooksFailed(true);

        toast.neutral(
          t('webhooksError', {
            defaultValue: 'Could not load the webhook subscriptions: {{errorMessage}}',
            errorMessage,
          }),
        );
      });
    }

    return () => {
      isCurrentPartner = false;
    };
    // toast is deliberately left out: the provider recreates its value every render, so depending
    // on it would refetch on every toast.
  }, [
    bearer,
    isExtendedInfoReadable,
    isWebhooksReadable,
    fetchPartnerExtendedInfo,
    fetchPartnerWebhookInfo,
    resetExtendedInfo,
    resetWebhooks,
    t,
  ]);

  // Undefined while loading, null when the request failed — every row renders each differently.
  const extendedInfo = partnerExtendedInfo ?? (hasExtendedInfoFailed ? null : undefined);
  const webhooks = partnerWebhooks ?? (hasWebhooksFailed ? null : undefined);

  const notProvidedYet = t('notProvidedYet', { defaultValue: 'Not provided yet' });
  const notAvailable = t('notAvailable', { defaultValue: 'Not available' });

  // A partner account the route refuses has nothing to show, which reads as "not available" — not
  // as a request that failed, since none was made.
  const extendedInfoField = (pick: (current: PartnerExtendedInfo) => string) =>
    renderDetailValue(
      isExtendedInfoReadable ? pickDetail(extendedInfo, pick) : '',
      isExtendedInfoReadable ? notProvidedYet : notAvailable,
    );

  const accountInfo: DetailRow[] = accountInfoRows.map(({ label, value }) => ({ label, value }));

  // One line, in postal order, from whichever parts the partner account has filled in; the province
  // as its code, which reads the same in both locales.
  const formatAddress = ({ AddressResponse: address }: PartnerExtendedInfo) =>
    [
      address.Address1,
      address.Address2,
      address.City,
      [address.ADRProvinces_ID && toProvinceCode(address.ADRProvinces_ID), address.PostalCode]
        .filter(Boolean)
        .join(' '),
    ]
      .filter(Boolean)
      .join(', ');

  // A partner account is created with its country and currency only (spec 5.6.1): both are on the
  // list row, so they never wait on a request; the rest is completed later.
  const businessDetails: DetailRow[] = [
    {
      label: t('BusinessDetails.legalBusinessName', { defaultValue: 'Legal Business Name' }),
      value: extendedInfoField((current) => current.AccountInformationResponse.LegalBusinessName),
    },
    {
      label: t('BusinessDetails.address', { defaultValue: 'Address' }),
      value: extendedInfoField(formatAddress),
    },
    {
      label: t('BusinessDetails.country', { defaultValue: 'Country' }),
      value: renderDetailValue(partner.Country && resolveCountryLabel(partner.Country)),
    },
    {
      label: t('BusinessDetails.currency', { defaultValue: 'Currency' }),
      value: renderDetailValue(partner.Currency),
    },
  ];

  const webhookRows: DetailRow[] = (webhooks ?? []).map(({ Event, Url }) => ({
    label: resolveWebhookEventLabel(Event),
    value: <span className={cn('url')}>{Url}</span>,
  }));

  const contactOf = (role: ContactRole): Contact | null | undefined => {
    if (!isExtendedInfoReadable) return BLANK_CONTACT;
    if (extendedInfo === undefined) return undefined;
    if (extendedInfo === null) return null;

    const prefix = CONTACT_PREFIXES[role];
    const info = extendedInfo.AccountInformationResponse;

    return {
      fullName: info[`${prefix}FullName`],
      occupation: info[`${prefix}Occupation`],
      title: info[`${prefix}Title`],
      email: info[`${prefix}Email`],
      phoneNumber: info[`${prefix}PhoneNumber`],
    };
  };

  return (
    <div className={cn('tab')} data-testid="partnerDetailsTab">
      <div className={cn('modules')}>
        <DetailsModule size="rg" title={t('AccountInfo.title', { defaultValue: 'Account Info' })}>
          <DetailRows className={cn('rows')} details={accountInfo} />
        </DetailsModule>

        <DetailsModule size="rg" title={t('BusinessDetails.title', { defaultValue: 'Business Details' })}>
          <DetailRows className={cn('rows')} details={businessDetails} />
        </DetailsModule>

        <DetailsModule size="rg" title={t('WebhookSubscriptions.title', { defaultValue: 'Webhook Subscriptions' })}>
          {!isWebhooksReadable && (
            <BodyText className={cn('cardMessage')}>
              {t('WebhookSubscriptions.inactive', {
                defaultValue: 'Webhook subscriptions become available once the partner account is Active.',
              })}
            </BodyText>
          )}

          {isWebhooksReadable && webhooks === undefined && (
            <Skeleton height={56} className={cn('cardMessage')} data-testid="webhooksSkeleton" />
          )}

          {isWebhooksReadable && webhooks === null && (
            <BodyText className={cn('cardMessage')}>
              {t('WebhookSubscriptions.unavailable', { defaultValue: 'The webhook subscriptions are unavailable.' })}
            </BodyText>
          )}

          {isWebhooksReadable && webhooks?.length === 0 && (
            <BodyText className={cn('cardMessage')}>
              {t('WebhookSubscriptions.empty', {
                defaultValue: 'This partner account has no webhook subscriptions yet.',
              })}
            </BodyText>
          )}

          {webhookRows.length > 0 && <DetailRows className={cn('rows')} details={webhookRows} />}
        </DetailsModule>
      </div>

      <section className={cn('contacts')} aria-label={t('Contacts.title', { defaultValue: 'Contact Information' })}>
        {/* Figma: H6/Title 15/Emphasized, on a 32px line (level with an Edit button that is a non-goal). */}
        <Title level="6" className={cn('contactsTitle')}>
          {t('Contacts.title', { defaultValue: 'Contact Information' })}
        </Title>

        {CONTACT_ROLES.map((role) => (
          <ContactCard
            key={role}
            role={role}
            size="rg"
            contact={contactOf(role)}
            withOccupation
            blankPlaceholder={isExtendedInfoReadable ? undefined : notAvailable}
          />
        ))}
      </section>
    </div>
  );
}
